// src/pages/BookingConfirmation.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./BookingConfirmation.css";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

function formatLocationLabel(raw) {
  const parts = String(raw || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "";
  const last = (parts[parts.length - 1] || "").toLowerCase();
  const isCountry = ["usa", "united states", "united states of america", "us", "u.s.a", "u.s."].includes(last);
  if (isCountry) parts.pop();
  return parts.slice(0, 3).join(", ");
}

export default function BookingConfirmation() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/booking/by-token/${encodeURIComponent(token)}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Failed to load booking.");
        if (!alive) return;
        setData(j);
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="pl-bc-container"><div className="pl-bc-card">Loading…</div></div>;
  if (err) return <div className="pl-bc-container"><div className="pl-bc-card"><div className="pl-bc-error">{err}</div></div></div>;

  const locationLabel = formatLocationLabel(data.location) || "Location to be shared by host";
  const displayTimeZone = data.timeZone || data.hostTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = data.startTime ? new Date(data.startTime) : null;
  const end = data.endTime ? new Date(data.endTime) : null;
  let dateLabel = data.localDate || "";
  let timeLabel = data.localTimeRange || "";
  if (start instanceof Date && end instanceof Date && !Number.isNaN(start) && !Number.isNaN(end)) {
    try {
      const dateFmt = new Intl.DateTimeFormat(undefined, { timeZone: displayTimeZone, weekday: "short", month: "short", day: "numeric", year: "numeric" });
      const timeFmt = new Intl.DateTimeFormat(undefined, { timeZone: displayTimeZone, hour: "numeric", minute: "2-digit" });
      dateLabel = dateFmt.format(start);
      timeLabel = `${timeFmt.format(start)} – ${timeFmt.format(end)}`;
    } catch (_) { /* fall back to server labels */ }
  }
  const normalizedStatus = String(data.acceptState || "pending").toLowerCase();
  const statusLabel = ["accepted", "accepted_needs_payout"].includes(normalizedStatus)
    ? "Accepted"
    : (data.acceptState || "pending").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const headerTitle = (() => {
    if (normalizedStatus === "declined") return "Cancelled";
    if (["accepted", "accepted_needs_payout"].includes(normalizedStatus)) return "Activity Confirmed";
    if (normalizedStatus === "completed") return "Activity Completed";
    return "Request Sent 🎉";
  })();
  const showChargeNote = normalizedStatus === "pending" || normalizedStatus === "declined";
  const chargeNoteText =
    normalizedStatus === "declined"
      ? "Your card has not been charged."
      : "A temporary hold may appear on your card. We only capture it if " + (data.creatorName || "the host") + " accepts; otherwise it is released.";

  const hasCustomerReview = Boolean(data.customerReview);
  const canReview = normalizedStatus === "completed" && !hasCustomerReview;

  const submitReview = async () => {
    if (!canReview) return;
    if (reviewRating < 1 || reviewRating > 5) {
      setReviewError("Please select a rating.");
      return;
    }
    if (reviewComment.length > 500) {
      setReviewError("Comment must be 500 characters or less.");
      return;
    }
    try {
      setReviewSaving(true);
      setReviewError("");
      const res = await fetch(`${API_BASE_URL}/api/booking/by-token/${encodeURIComponent(token)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment })
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || "Could not submit review.");
      setData(prev => ({ ...(prev || {}), customerReview: { rating: reviewRating, comment: reviewComment, createdAt: new Date().toISOString() } }));
    } catch (e) {
      setReviewError(e.message || "Could not submit review.");
    } finally {
      setReviewSaving(false);
    }
  };
  const totalLabel = data.extraCostType === "paid_separately"
    ? `$${(data.amount / 100).toFixed(2)} + activity cost`
    : `$${(data.amount / 100).toFixed(2)}`;
  const activityDescription = (data.activityDescription || "").trim();
  const customerMessage = (data.customerIntro || "").trim();
  const extraCostNote = (data.extraCostNote || "").trim();
  const showExtraCostNote = ["paid_separately", "paid_separetly"].includes(data.extraCostType) && extraCostNote;
  const showReviewNote = ["accepted", "accepted_needs_payout"].includes(String(data.acceptState || "").toLowerCase());

  return (
    <div className="pl-bc-container">
      <div className="pl-bc-card">
        <div className="pl-bc-head">
          <h1>{headerTitle}</h1>
          <p>We've sent a confirmation to {data.customerEmail}.</p>
          <p>Use this page to check the status. We'll update you by email if anything changes.</p>
        </div>
        <div className="pl-bc-section">
          <div className="pl-bc-subtitle">Request Summary</div>
          <div className="pl-bc-row"><span>Host:</span><span>{data.creatorName}</span></div>
          <div className="pl-bc-row"><span>Activity:</span><span>{data.activityTitle}</span></div>
          <div className="pl-bc-row"><span>Location:</span><span>{locationLabel}</span></div>
          <div className="pl-bc-row"><span>Date:</span><span>{dateLabel}</span></div>
          <div className="pl-bc-row"><span>Time:</span><span>{timeLabel}</span></div>
          <div className="pl-bc-row"><span>Status:</span><span>{statusLabel}</span></div>
          <div className="pl-bc-row"><span>Total:</span><span>{totalLabel}</span></div>
          {showChargeNote && <div className="pl-bc-note">{chargeNoteText}</div>}
        </div>
        <div className="pl-bc-section pl-bc-section-stack">
          <div className="pl-bc-subtitle">Activity Description</div>
          <div className="pl-bc-softcard">{activityDescription || "No description provided."}</div>
        </div>
        {showExtraCostNote && (
          <div className="pl-bc-section pl-bc-section-stack">
            <div className="pl-bc-subtitle">Extra Cost Note</div>
            <div className="pl-bc-softcard">{`${extraCostNote}`}</div>
          </div>
        )}
        <div className="pl-bc-section pl-bc-section-stack">
          <div className="pl-bc-subtitle">Your Message</div>
          <div className="pl-bc-softcard">{customerMessage ? `"${customerMessage}"` : `No message provided.`}</div>
        </div>
        {showReviewNote && (
          <div className="pl-bc-review-note">
            You will be able to review on this page when the status is Completed.
          </div>
        )}
        {normalizedStatus === "completed" && (
          <div className="pl-bc-review-box">
            <div className="pl-bc-subtitle">Leave a Review</div>
            {data.customerReview ? (
              <div className="pl-bc-softcard">
                <div className="pl-bc-review-stars">Rating: {data.customerReview.rating}/5</div>
                {data.customerReview.comment && <div className="pl-bc-text-plain pl-bc-review-comment">"{data.customerReview.comment}"</div>}
              </div>
            ) : (
              <>
                <div className="pl-bc-review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`pl-bc-star ${reviewRating >= n ? "is-active" : ""}`}
                      onClick={() => setReviewRating(n)}
                      disabled={reviewSaving}
                      aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <div className="pl-bc-review-input-wrap">
                  <textarea
                    className="pl-bc-review-textarea"
                    placeholder="Share a short comment (optional)"
                    maxLength={500}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    disabled={reviewSaving}
                  />
                  <div className="pl-bc-review-charcount-inline">{reviewComment.length}/500</div>
                </div>
                <div className="pl-bc-review-actions">
                  <button className="pl-bc-review-btn" onClick={submitReview} disabled={reviewSaving}>
                    {reviewSaving ? "Submitting..." : "Submit Review"}
                  </button>
                </div>
                {reviewError && <div className="pl-bc-error">{reviewError}</div>}
              </>
            )}
          </div>
        )}
        <div className="pl-bc-footer">Need help? Email us at support@activitybuddy.com</div>
      </div>
    </div>
  );
}
